/**
 * Composant de gestion des devices de connexion
 *
 * ISO 27001 A.9.4.2 - Procédures de connexion sécurisée
 * Affiche tous les devices utilisés pour se connecter et permet de les gérer
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { LoginDevice } from '@/utils/api';
import { confirm } from '@/stores/confirmStore';
import {
  useDevices,
  useTrustDevice,
  useRevokeDevice,
  useDeleteDevice,
  useRevokeAllDevices,
} from '../hooks/useDevices';
import {
  Monitor,
  Smartphone,
  Tablet,
  HelpCircle,
  Shield,
  ShieldOff,
  Trash2,
  LogOut,
  Clock,
  MapPin,
  RefreshCw,
} from 'lucide-react';

// Icône pour le type de device
const DeviceIcon = ({ type }: { type: LoginDevice['deviceType'] }) => {
  const iconProps = { className: 'w-6 h-6 text-teal-600' };

  switch (type) {
    case 'desktop':
      return <Monitor {...iconProps} />;
    case 'mobile':
      return <Smartphone {...iconProps} />;
    case 'tablet':
      return <Tablet {...iconProps} />;
    default:
      return <HelpCircle {...iconProps} />;
  }
};

// Formater une date relative
const formatRelativeDate = (dateStr: string, t: TFunction): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return t('auth.devices.justNow');
  if (diffMinutes < 60) return t('auth.devices.minutesAgo', { count: diffMinutes });
  if (diffHours < 24) return t('auth.devices.hoursAgo', { count: diffHours });
  if (diffDays < 7) return diffDays > 1
    ? t('auth.devices.daysAgoPlural', { count: diffDays })
    : t('auth.devices.daysAgo', { count: diffDays });

  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

interface DeviceCardProps {
  device: LoginDevice;
  isCurrentDevice: boolean;
  onTrust: (deviceId: string) => void;
  onRevoke: (deviceId: string) => void;
  onDelete: (deviceId: string) => void;
  loading: boolean;
}

const DeviceCard: React.FC<DeviceCardProps> = ({
  device,
  isCurrentDevice,
  onTrust,
  onRevoke,
  onDelete,
  loading,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={`
        bg-white/60 backdrop-blur-sm border rounded-2xl p-5 transition-all duration-300 hover:bg-white/80
        ${device.isTrusted ? 'border-teal-200' : 'border-gray-200'}
        ${isCurrentDevice ? 'ring-2 ring-teal-500/30' : ''}
      `}
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className={`
            w-12 h-12 rounded-xl flex items-center justify-center
            ${device.isTrusted ? 'bg-teal-100' : 'bg-gray-100'}
          `}>
            <DeviceIcon type={device.deviceType} />
          </div>

          <div>
            <h4 className="font-semibold text-gray-900">{device.deviceName}</h4>
            <p className="text-sm text-gray-500">{device.browser} • {device.os}</p>
          </div>
        </div>

        {/* Badges */}
        <div className="flex items-center gap-2">
          {isCurrentDevice && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 rounded-full">
              <Monitor className="w-3.5 h-3.5 text-blue-700" />
              <span className="text-xs font-medium text-blue-700">{t('auth.devices.currentDevice')}</span>
            </div>
          )}
          {device.isTrusted && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-100 rounded-full">
              <Shield className="w-3.5 h-3.5 text-teal-700" />
              <span className="text-xs font-medium text-teal-700">{t('auth.devices.trusted')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div className="flex items-center gap-2 text-gray-600">
          <Clock className="w-4 h-4 text-gray-400" />
          <span>{t('auth.devices.lastActivityLabel')} {formatRelativeDate(device.lastSeenAt, t)}</span>
        </div>

        <div className="flex items-center gap-2 text-gray-600">
          <RefreshCw className="w-4 h-4 text-gray-400" />
          <span>{t('auth.devices.sessionsCount', { count: device.loginCount })}</span>
        </div>

        {device.lastIpAddress && (
          <div className="flex items-center gap-2 text-gray-600">
            <MapPin className="w-4 h-4 text-gray-400" />
            <span>IP: {device.lastIpAddress}</span>
          </div>
        )}

        {device.trustExpiresAt && (
          <div className="flex items-center gap-2 text-gray-600">
            <Clock className="w-4 h-4 text-gray-400" />
            <span>{t('auth.ipWhitelist.expires')} {formatRelativeDate(device.trustExpiresAt, t)}</span>
          </div>
        )}
      </div>

      {/* First seen date */}
      <p className="mt-3 text-xs text-gray-400">
        {t('auth.devices.firstConnection')} {new Date(device.firstSeenAt).toLocaleDateString('fr-FR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })}
      </p>

      {/* Actions */}
      <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end gap-2">
        {device.isTrusted ? (
          <button
            onClick={() => onRevoke(device.id)}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-amber-700 hover:bg-amber-50 rounded-xl transition-colors disabled:opacity-50"
          >
            <ShieldOff className="w-4 h-4" />
            {t('auth.devices.revokeTrust')}
          </button>
        ) : (
          <button
            onClick={() => onTrust(device.id)}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-teal-700 hover:bg-teal-50 rounded-xl transition-colors disabled:opacity-50"
          >
            <Shield className="w-4 h-4" />
            {t('auth.devices.trust')}
          </button>
        )}

        <button
          onClick={() => onDelete(device.id)}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors disabled:opacity-50"
        >
          <Trash2 className="w-4 h-4" />
          {t('auth.devices.delete')}
        </button>
      </div>
    </div>
  );
};

export const DevicesManagement: React.FC = () => {
  const { t } = useTranslation();
  // TanStack Query hooks
  const { data, isLoading: loading, error: queryError } = useDevices();
  const trustDevice = useTrustDevice();
  const revokeDevice = useRevokeDevice();
  const deleteDevice = useDeleteDevice();
  const revokeAllDevices = useRevokeAllDevices();

  // Extract data from query
  const devices = data?.devices ?? [];
  const currentDeviceId = data?.currentDeviceId ?? null;

  // Combined loading state for mutations
  const actionLoading =
    trustDevice.isPending ||
    revokeDevice.isPending ||
    deleteDevice.isPending ||
    revokeAllDevices.isPending;

  // Error state
  const [localError, setLocalError] = useState<string | null>(null);
  const error = localError || (queryError ? t('auth.devices.loadError') : null);

  const handleTrust = async (deviceId: string) => {
    setLocalError(null);
    try {
      await trustDevice.mutateAsync({ deviceId, days: 90 });
    } catch {
      setLocalError(t('auth.devices.trustError'));
    }
  };

  const handleRevoke = async (deviceId: string) => {
    setLocalError(null);
    try {
      await revokeDevice.mutateAsync(deviceId);
    } catch {
      setLocalError(t('auth.devices.revokeError'));
    }
  };

  const handleDelete = async (deviceId: string) => {
    const confirmed = await confirm({
      title: t('auth.devices.confirmDeleteTitle'),
      message: t('auth.devices.confirmDeleteText'),
      variant: 'danger',
      confirmText: t('auth.devices.delete'),
      cancelText: t('auth.changePassword.cancel'),
    });
    if (!confirmed) {
      return;
    }

    setLocalError(null);
    try {
      await deleteDevice.mutateAsync(deviceId);
    } catch {
      setLocalError(t('auth.devices.deleteError'));
    }
  };

  const handleRevokeAll = async () => {
    const confirmed = await confirm({
      title: t('auth.devices.confirmRevokeAllTitle'),
      message: t('auth.devices.confirmRevokeAllText'),
      variant: 'warning',
      confirmText: t('auth.devices.confirmRevokeAllButton'),
      cancelText: t('auth.changePassword.cancel'),
    });
    if (!confirmed) {
      return;
    }

    setLocalError(null);
    try {
      await revokeAllDevices.mutateAsync();
    } catch {
      setLocalError(t('auth.devices.revokeError'));
    }
  };

  if (loading) {
    return (
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl border border-white/30 shadow-xl shadow-gray-300/10 p-8">
        <div className="flex items-center gap-4 mb-8">
          <div className="p-4 bg-gradient-to-br from-teal-50 to-teal-100/50 rounded-2xl shadow-sm">
            <Monitor className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('auth.devices.title')}</h2>
            <p className="text-sm text-gray-500">{t('auth.devices.subtitle')}</p>
          </div>
        </div>
        <div className="space-y-4">
          <div className="h-32 bg-white/30 rounded-2xl animate-pulse" />
          <div className="h-32 bg-white/30 rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/20 backdrop-blur-xl rounded-3xl border border-white/30 shadow-xl shadow-gray-300/10 p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="p-4 bg-gradient-to-br from-teal-50 to-teal-100/50 rounded-2xl shadow-sm">
            <Monitor className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('auth.devices.title')}</h2>
            <p className="text-sm text-gray-500">
              {t('auth.devices.subtitle')}
            </p>
          </div>
        </div>

        {devices.some(d => d.isTrusted) && (
          <button
            onClick={handleRevokeAll}
            disabled={actionLoading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-amber-700 hover:bg-amber-50 rounded-xl transition-colors disabled:opacity-50"
          >
            <LogOut className="w-4 h-4" />
            {t('auth.devices.revokeAllShort')}
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50/80 border border-red-200 rounded-2xl text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Content */}
      {devices.length === 0 ? (
        <div className="text-center py-12">
          <Monitor className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-700">{t('auth.devices.noDevices')}</h4>
          <p className="text-sm text-gray-500 mt-1">
            {t('auth.devices.noDevicesSubtitle')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {devices.map((device) => (
            <DeviceCard
              key={device.id}
              device={device}
              isCurrentDevice={device.id === currentDeviceId}
              onTrust={handleTrust}
              onRevoke={handleRevoke}
              onDelete={handleDelete}
              loading={actionLoading}
            />
          ))}
        </div>
      )}

      {/* Info */}
      <div className="mt-8 p-5 bg-white/40 backdrop-blur-sm border border-teal-100 rounded-2xl">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700">
            <p className="font-semibold text-gray-900">{t('auth.devices.trustInfo')}</p>
            <p className="text-gray-600 mt-1">
              {t('auth.devices.trustInfoText')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevicesManagement;
